import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  DollarSign, Users, FileText, TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle, Clock, XCircle, CreditCard, Droplet, Search, Filter,
  Download, Plus, Eye, Activity, BarChart3, PieChart, IndianRupee
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import Breadcrumb from './Breadcrumb';
import { useAuth } from '../contexts/AuthContext';

const BillingRevenue = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [consumers, setConsumers] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [revenueLeaks, setRevenueLeaks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBillStatus, setSelectedBillStatus] = useState('all');
  const [selectedLeakType, setSelectedLeakType] = useState('all');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [statsRes, consumersRes, billsRes, paymentsRes, leaksRes] = await Promise.all([
        api.billing.getStats(),
        api.billing.getConsumers(),
        api.billing.getBills(),
        api.billing.getPayments(),
        api.billing.getRevenueLeaks()
      ]);

      setStats(statsRes.data);
      setConsumers(consumersRes.data.data || []);
      setBills(billsRes.data.data || []);
      setPayments(paymentsRes.data.data || []);
      setRevenueLeaks(leaksRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Paid': 'text-green-400 bg-green-500/20 border-green-500/50',
      'PartiallyPaid': 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50',
      'Overdue': 'text-red-400 bg-red-500/20 border-red-500/50',
      'Generated': 'text-blue-400 bg-blue-500/20 border-blue-500/50',
      'Sent': 'text-cyan-400 bg-cyan-500/20 border-cyan-500/50',
      'Active': 'text-green-400 bg-green-500/20 border-green-500/50',
      'Inactive': 'text-gray-400 bg-gray-500/20 border-gray-500/50',
      'Critical': 'text-red-400 bg-red-500/20 border-red-500/50',
      'High': 'text-orange-400 bg-orange-500/20 border-orange-500/50',
      'Medium': 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50',
      'Low': 'text-blue-400 bg-blue-500/20 border-blue-500/50',
      'Detected': 'text-red-400 bg-red-500/20 border-red-500/50',
      'UnderInvestigation': 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50',
      'ActionTaken': 'text-blue-400 bg-blue-500/20 border-blue-500/50',
      'Resolved': 'text-green-400 bg-green-500/20 border-green-500/50'
    };
    return colors[status] || 'text-gray-400 bg-gray-500/20 border-gray-500/50';
  };

  const filteredConsumers = consumers.filter(c => 
    selectedCategory === 'all' || c.category === selectedCategory
  );

  const filteredBills = bills.filter(b =>
    selectedBillStatus === 'all' || b.status === selectedBillStatus
  );

  const filteredLeaks = revenueLeaks.filter(l =>
    selectedLeakType === 'all' || l.leak_type === selectedLeakType
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-6"></div>
            <div className="absolute inset-0 w-20 h-20 mx-auto">
              <IndianRupee className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
          </div>
          <p className="text-cyan-400 font-semibold tracking-wide">LOADING BILLING DATA...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Billing & Revenue Assurance' }]} onNavigate={() => {}} />
      
      {/* Header */}
      <div className="relative rounded-xl overflow-hidden border border-cyan-500/30 bg-gradient-to-br from-[#12172e] via-[#0f1629] to-[#12172e] p-6">
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center gap-3">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                  BILLING & REVENUE ASSURANCE
                </span>
                <IndianRupee className="text-cyan-400" size={32} />
              </h1>
              <p className="text-gray-400">Revenue Management • Collection Monitoring • Leak Detection</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'consumers', label: 'Consumers', icon: Users },
          { id: 'bills', label: 'Bills & Payments', icon: FileText },
          { id: 'leaks', label: 'Revenue Leaks', icon: AlertTriangle }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-cyan-500/20 to-teal-500/20 border border-cyan-500/50 text-cyan-400'
                  : 'bg-[#12172e] border border-gray-700 text-gray-400 hover:border-cyan-500/30'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && stats && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 font-medium">TOTAL BILLED</p>
                    <p className="text-3xl font-bold text-cyan-400 mt-2">{formatCurrency(stats.revenue.total_billed)}</p>
                    <p className="text-xs text-gray-500 mt-1">{stats.bills.total} bills</p>
                  </div>
                  <DollarSign className="text-cyan-400" size={40} />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#12172e] border-green-500/30 hover:border-green-500/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 font-medium">COLLECTED</p>
                    <p className="text-3xl font-bold text-green-400 mt-2">{formatCurrency(stats.revenue.total_collected)}</p>
                    <p className="text-xs text-gray-500 mt-1">{stats.bills.paid} paid</p>
                  </div>
                  <CheckCircle className="text-green-400" size={40} />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#12172e] border-red-500/30 hover:border-red-500/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 font-medium">OUTSTANDING</p>
                    <p className="text-3xl font-bold text-red-400 mt-2">{formatCurrency(stats.revenue.total_outstanding)}</p>
                    <p className="text-xs text-gray-500 mt-1">{stats.bills.overdue} overdue</p>
                  </div>
                  <Clock className="text-red-400" size={40} />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#12172e] border-yellow-500/30 hover:border-yellow-500/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 font-medium">COLLECTION RATE</p>
                    <p className="text-3xl font-bold text-yellow-400 mt-2">{stats.revenue.collection_efficiency}%</p>
                    <p className="text-xs text-gray-500 mt-1">efficiency</p>
                  </div>
                  <TrendingUp className="text-yellow-400" size={40} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[#12172e] border-cyan-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <PieChart size={20} className="text-cyan-400" />
                  Consumers by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.consumers.by_category).map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <span className="text-white">{category}</span>
                      <span className="text-cyan-400 font-bold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#12172e] border-red-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle size={20} className="text-red-400" />
                  Revenue Leaks
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">Total Leaks</p>
                    <p className="text-2xl font-bold text-red-400">{stats.revenue_leaks.total}</p>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">Critical</p>
                    <p className="text-2xl font-bold text-orange-400">{stats.revenue_leaks.critical}</p>
                  </div>
                </div>
                <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                  <p className="text-xs text-gray-400 mb-1">Est. Monthly Loss</p>
                  <p className="text-3xl font-bold text-red-400">{formatCurrency(stats.revenue_leaks.estimated_monthly_loss)}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-[#12172e] border-cyan-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Active Consumers</h3>
                  <Users className="text-cyan-400" size={24} />
                </div>
                <p className="text-4xl font-bold text-cyan-400">{stats.consumers.active}</p>
                <p className="text-sm text-gray-400 mt-2">of {stats.consumers.total} total</p>
              </CardContent>
            </Card>

            <Card className="bg-[#12172e] border-yellow-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Pending Bills</h3>
                  <Clock className="text-yellow-400" size={24} />
                </div>
                <p className="text-4xl font-bold text-yellow-400">{stats.bills.pending}</p>
                <p className="text-sm text-gray-400 mt-2">awaiting payment</p>
              </CardContent>
            </Card>

            <Card className="bg-[#12172e] border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Paid Bills</h3>
                  <CheckCircle className="text-green-400" size={24} />
                </div>
                <p className="text-4xl font-bold text-green-400">{stats.bills.paid}</p>
                <p className="text-sm text-gray-400 mt-2">completed</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'consumers' && (
        <div className="space-y-6">
          {/* Filter */}
          <Card className="bg-[#12172e] border-cyan-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-400 font-medium">CATEGORY:</span>
                {['all', 'Residential', 'Commercial', 'Industrial', 'Government'].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedCategory === cat
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                        : 'bg-slate-800/50 text-gray-400 border border-slate-700/50'
                    }`}
                  >
                    {cat.toUpperCase()}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Consumers Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredConsumers.map((consumer) => (
              <Card key={consumer.id} className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{consumer.name}</h3>
                      <p className="text-sm text-gray-400">{consumer.consumer_number}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(consumer.status)}`}>
                      {consumer.status}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Category:</span>
                      <span className="text-sm text-white font-medium">{consumer.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">Tariff:</span>
                      <span className="text-sm text-green-400">{formatCurrency(consumer.tariff_rate)}/KL</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Avg. Monthly</span>
                      <span className="text-sm font-semibold text-blue-400">{consumer.average_monthly_consumption?.toFixed(1)} KL</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'bills' && (
        <div className="space-y-6">
          {/* Filter */}
          <Card className="bg-[#12172e] border-cyan-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm text-gray-400 font-medium">STATUS:</span>
                {['all', 'Generated', 'Sent', 'Paid', 'PartiallyPaid', 'Overdue'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setSelectedBillStatus(status)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedBillStatus === status
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                        : 'bg-slate-800/50 text-gray-400 border border-slate-700/50'
                    }`}
                  >
                    {status === 'all' ? 'ALL' : status.toUpperCase()}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bills Table */}
          <Card className="bg-[#12172e] border-cyan-500/30">
            <CardHeader>
              <CardTitle className="text-white">Bills Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Bill Number</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Consumer</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Amount</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Paid</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Outstanding</th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBills.slice(0, 20).map((bill) => (
                      <tr key={bill.id} className="border-b border-slate-800 hover:bg-slate-900/50">
                        <td className="py-3 px-4 text-cyan-400 font-mono text-xs">{bill.bill_number}</td>
                        <td className="py-3 px-4 text-white">{bill.consumer_number}</td>
                        <td className="py-3 px-4 text-right text-white font-semibold">{formatCurrency(bill.total_amount)}</td>
                        <td className="py-3 px-4 text-right text-green-400">{formatCurrency(bill.amount_paid)}</td>
                        <td className="py-3 px-4 text-right text-red-400">{formatCurrency(bill.amount_outstanding)}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(bill.status)}`}>
                            {bill.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredBills.length > 20 && (
                <div className="mt-4 text-center text-sm text-gray-400">
                  Showing 20 of {filteredBills.length} bills
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Payments */}
          <Card className="bg-[#12172e] border-green-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard size={20} className="text-green-400" />
                Recent Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {payments.slice(0, 10).map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-white">{payment.payment_reference}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {payment.payment_method} • {formatDate(payment.payment_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-400">{formatCurrency(payment.amount)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'leaks' && (
        <div className="space-y-6">
          {/* Filter */}
          <Card className="bg-[#12172e] border-red-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm text-gray-400 font-medium">TYPE:</span>
                {['all', 'UnbilledConsumption', 'UnpaidBills', 'MeterTampering', 'BillingError'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setSelectedLeakType(type)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedLeakType === type
                        ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                        : 'bg-slate-800/50 text-gray-400 border border-slate-700/50'
                    }`}
                  >
                    {type === 'all' ? 'ALL' : type.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Leaks Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredLeaks.map((leak) => (
              <Card key={leak.id} className="bg-[#12172e] border-red-500/30 hover:border-red-500/50 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(leak.leak_type)}`}>
                      {leak.leak_type.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(leak.severity)}`}>
                      {leak.severity}
                    </span>
                  </div>

                  <p className="text-white mb-4">{leak.description}</p>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                      <span className="text-sm text-gray-400">Est. Monthly Loss</span>
                      <span className="text-lg font-bold text-red-400">{formatCurrency(leak.estimated_loss_monthly)}</span>
                    </div>

                    {leak.recovery_amount > 0 && (
                      <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                        <span className="text-sm text-gray-400">Recovery</span>
                        <span className="text-lg font-bold text-green-400">{formatCurrency(leak.recovery_amount)}</span>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-slate-700">
                    <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(leak.status)}`}>
                      {leak.status.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingRevenue;
